import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { 
  FaCrown, FaFileCsv, FaSync, FaSearch, FaLock, FaSpinner, FaServer, 
  FaMoneyBillWave, FaUserSecret, FaWhatsapp, FaBullhorn, FaAddressBook, FaTimes, FaIdBadge 
} from 'react-icons/fa';

// --- GOD MODE THEME ---
const THEME = {
  bg: '#020617',        // Deepest Slate
  card: '#0f172a',      // Dark Slate
  border: '#1e293b',    // Border
  primary: '#3b82f6',   // Blue
  success: '#10b981',   // Green
  warning: '#f59e0b',   // Gold
  danger: '#ef4444',    // Red
  textMain: '#f8fafc',
  textSub: '#94a3b8'
};

function SuperAdmin() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ==========================================
  // 🔐 THE MASTER SECURITY LOCK
  // ==========================================
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // ⚠️ REPLACE THIS WITH YOUR REAL ADMIN EMAIL(S)
  const MASTER_ADMIN_EMAILS = ["mallorcaluxuryspa@gmail.com", "admin@tesla.com"]; 

  // Platform Data
  const [platformMetrics, setPlatformMetrics] = useState({ 
    totalUsers: 0, globalSent: 0, globalSpend: 0, mrr: 0, activeApis: 0 
  });
  const [allBusinesses, setAllBusinesses] = useState([]);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('spend_desc');

  // Drill-down Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalTab, setModalTab] = useState('campaigns'); 
  const [userLogs, setUserLogs] = useState([]);
  const [userAudience, setUserAudience] = useState([]);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // Business Assumptions (INR)
  const COST_PER_MSG = 0.80; 
  const MONTHLY_SAAS_FEE = 2999; 

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      if (u) {
        if (MASTER_ADMIN_EMAILS.includes(u.email)) {
          setIsAuthorized(true);
          fetchGlobalData();
        } else {
          setIsAuthorized(false);
          setLoading(false);
        }
      } else {
        setIsAuthorized(false);
        setLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const fetchGlobalData = async () => {
    setLoading(true);
    try {
      // 1. Fetch All Businesses
      const businessesSnapshot = await getDocs(collection(db, "businesses"));
      let globalSentCount = 0;
      let activeApiCount = 0;
      const businessArray = [];

      // 2. Iterate & Calculate Stats per Business
      for (const docSnap of businessesSnapshot.docs) {
        const bData = docSnap.data();
        const uid = docSnap.id;
        
        // --- SMART IDENTITY DETECTION ---
        // Tries to find ANY recognizable name/email
        let identity = "Unknown User";
        let subIdentity = "No details";

        if (bData.email) {
            identity = bData.email;
            subIdentity = bData.businessName || bData.name || "No Business Name";
        } else if (bData.businessName) {
            identity = bData.businessName;
            subIdentity = "No Email Provided";
        } else if (bData.name) {
            identity = bData.name;
            subIdentity = "Personal Account";
        } else if (bData.phoneNumber) {
            identity = bData.phoneNumber;
            subIdentity = "Phone Only";
        } else {
            identity = `Tenant ${uid.substring(0,6)}`;
            subIdentity = "Unidentified";
        }

        // Fetch sub-collections for accurate usage stats
        const broadcastsSnap = await getDocs(collection(db, "businesses", uid, "broadcasts"));
        let userSentCount = 0; 
        let campaignCount = 0;

        broadcastsSnap.forEach(bDoc => {
          campaignCount++;
          userSentCount += (bDoc.data().sent || 0);
        });

        globalSentCount += userSentCount;
        if (bData.accessToken) activeApiCount++;

        businessArray.push({
          uid: uid,
          displayName: identity, // The smart name
          subDetail: subIdentity, // Extra info
          phoneId: (bData.phoneId || bData.phoneNumberId) ? "Connected" : "Disconnected",
          totalCampaigns: campaignCount,
          totalSent: userSentCount,
          spend: userSentCount * COST_PER_MSG,
          status: bData.isSuspended ? "Suspended" : "Active",
        });
      }

      setAllBusinesses(businessArray);
      setPlatformMetrics({
        totalUsers: businessArray.length,
        globalSent: globalSentCount,
        globalSpend: globalSentCount * COST_PER_MSG,
        mrr: businessArray.length * MONTHLY_SAAS_FEE,
        activeApis: activeApiCount
      });
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
    setLoading(false);
  };

  // --- EXPORT FUNCTIONS ---
  const exportNetworkCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Identity,Sub-Detail,UID,API Connection,Campaigns,Messages Sent,Cost (INR),Status\n";
    allBusinesses.forEach(b => {
      const row = `${b.displayName},${b.subDetail},${b.uid},${b.phoneId},${b.totalCampaigns},${b.totalSent},${b.spend.toFixed(2)},${b.status}`;
      csvContent += row + "\n";
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Master_Network_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportTenantData = () => {
    if(!selectedUser) return;
    let csvContent = "data:text/csv;charset=utf-8,";
    if (modalTab === 'campaigns') {
        csvContent += "Date,Campaign Name,Audience Size,Delivered\n";
        userLogs.forEach(log => {
            csvContent += `${log.date},${log.name},${log.totalContacts},${log.sent}\n`;
        });
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", `${selectedUser.displayName}_campaigns.csv`);
        document.body.appendChild(link);
        link.click();
    } else {
        csvContent += "Phone,Last Active,Source,Last Message\n";
        userAudience.forEach(c => {
            csvContent += `${c.phone},${c.date},${c.source},"${c.lastMessage}"\n`;
        });
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", `${selectedUser.displayName}_audience.csv`);
        document.body.appendChild(link);
        link.click();
    }
  };

  const filteredAndSortedBusinesses = useMemo(() => {
    let result = [...allBusinesses];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(b => b.displayName.toLowerCase().includes(term) || b.uid.toLowerCase().includes(term));
    }
    switch (sortBy) {
      case 'spend_desc': result.sort((a, b) => b.spend - a.spend); break;
      case 'msgs_desc': result.sort((a, b) => b.totalSent - a.totalSent); break;
      case 'status': result.sort((a, b) => a.phoneId.localeCompare(b.phoneId)); break;
      default: break;
    }
    return result;
  }, [allBusinesses, searchTerm, sortBy]);

  const openUserLogs = async (business) => {
    setSelectedUser(business);
    setModalTab('campaigns');
    setIsModalLoading(true);
    try {
      const qCamps = query(collection(db, "businesses", business.uid, "broadcasts"), orderBy("createdAt", "desc"));
      const logsSnap = await getDocs(qCamps);
      setUserLogs(logsSnap.docs.map(doc => ({ 
          id: doc.id, ...doc.data(), 
          date: doc.data().createdAt ? doc.data().createdAt.toDate().toLocaleDateString() : 'Unknown' 
      })));

      const qContacts = query(collection(db, "businesses", business.uid, "contacts"), orderBy("lastActive", "desc"), limit(500));
      const contactsSnap = await getDocs(qContacts);
      setUserAudience(contactsSnap.docs.map(doc => ({ 
          id: doc.id, ...doc.data(), 
          date: doc.data().lastActive ? doc.data().lastActive.toDate().toLocaleDateString() : 'Unknown' 
      })));
    } catch (error) { console.error(error); }
    setIsModalLoading(false);
  };

  if (!user) return <div style={{ padding: '20px', background: THEME.bg, color: 'white', minHeight: '100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><FaSpinner className="fa-spin" size={30} /></div>;
  
  if (!loading && !isAuthorized) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#020617', color: '#f8fafc', textAlign: 'center' }}>
        <FaLock style={{ fontSize: '60px', color: THEME.danger, marginBottom: '20px' }} />
        <h1 style={{ margin: '0 0 10px 0', fontFamily:'monospace' }}>ACCESS DENIED</h1>
        <p style={{ color: THEME.textSub, maxWidth:'400px' }}>Your neural link ({user.email}) is not authorized for the God Mode Console.</p>
        <button onClick={() => window.location.href = '/'} style={{ marginTop: '30px', background: THEME.primary, color: 'white', padding: '12px 30px', border: 'none', borderRadius: '50px', cursor: 'pointer', fontWeight:'bold' }}>Return to Safety</button>
      </div>
    );
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: THEME.bg, color: THEME.textSub }}>
        <FaServer size={40} className="fa-spin" style={{ marginBottom: '20px', color: THEME.primary }} />
        <p>Establishing Secure Uplink to Global Database...</p>
    </div>
  );

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', padding: '30px', background: THEME.bg, minHeight: '100vh', color: THEME.textMain }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', borderBottom: `1px solid ${THEME.border}`, paddingBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 5px 0', color: THEME.textMain, fontSize: '28px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaCrown style={{ color: '#fbbf24' }} /> GOD MODE CONSOLE
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', color: THEME.textSub, fontSize: '12px', marginTop: '8px', fontFamily:'monospace' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span className="blink" style={{ width: '8px', height: '8px', background: THEME.success, borderRadius: '50%', boxShadow: `0 0 10px ${THEME.success}` }}></span> SYSTEM ONLINE</span>
            <span>|</span>
            <span>ADMIN: {user.email}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={exportNetworkCSV} style={{ background: '#1e293b', color: THEME.success, border: `1px solid ${THEME.success}`, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize:'13px', transition: '0.2s' }}>
            <FaFileCsv /> GLOBAL EXPORT
          </button>
          <button onClick={fetchGlobalData} style={{ background: THEME.primary, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize:'13px', boxShadow: '0 0 15px rgba(59, 130, 246, 0.4)' }}>
            <FaSync /> RELOAD DATA
          </button>
        </div>
      </div>

      {/* KPI METRICS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        {/* KPI CARDS (SAME AS BEFORE) */}
        <div style={{ background: THEME.card, padding: '25px', borderRadius: '16px', border: `1px solid ${THEME.border}`, position:'relative', overflow:'hidden' }}>
          <div style={{ position:'relative', zIndex:2 }}>
            <p style={{ margin: '0 0 5px 0', color: THEME.textSub, fontSize: '12px', fontWeight: '700', textTransform:'uppercase' }}>Active Tenants</p>
            <h2 style={{ margin: 0, color: 'white', fontSize: '36px', fontWeight: '800' }}>{platformMetrics.totalUsers}</h2>
            <p style={{ margin: '5px 0 0 0', fontSize:'12px', color: THEME.success }}>{platformMetrics.activeApis} APIs Connected</p>
          </div>
          <FaUserSecret style={{ position:'absolute', right:'-10px', bottom:'-10px', fontSize:'80px', color:'white', opacity:0.03 }} />
        </div>
        <div style={{ background: THEME.card, padding: '25px', borderRadius: '16px', border: `1px solid ${THEME.border}`, position:'relative', overflow:'hidden' }}>
          <div style={{ position:'relative', zIndex:2 }}>
            <p style={{ margin: '0 0 5px 0', color: THEME.textSub, fontSize: '12px', fontWeight: '700', textTransform:'uppercase' }}>Network Throughput</p>
            <h2 style={{ margin: 0, color: '#38bdf8', fontSize: '36px', fontWeight: '800' }}>{platformMetrics.globalSent.toLocaleString()}</h2>
            <p style={{ margin: '5px 0 0 0', fontSize:'12px', color: THEME.textSub }}>Messages Blasted</p>
          </div>
          <FaBullhorn style={{ position:'absolute', right:'-10px', bottom:'-10px', fontSize:'80px', color:'#38bdf8', opacity:0.05 }} />
        </div>
        <div style={{ background: THEME.card, padding: '25px', borderRadius: '16px', border: `1px solid ${THEME.border}`, position:'relative', overflow:'hidden' }}>
          <div style={{ position:'relative', zIndex:2 }}>
            <p style={{ margin: '0 0 5px 0', color: THEME.textSub, fontSize: '12px', fontWeight: '700', textTransform:'uppercase' }}>Est. SaaS MRR</p>
            <h2 style={{ margin: 0, color: '#fbbf24', fontSize: '36px', fontWeight: '800' }}>₹{(platformMetrics.mrr/1000).toFixed(1)}k</h2>
            <p style={{ margin: '5px 0 0 0', fontSize:'12px', color: '#fcd34d' }}>Monthly Revenue</p>
          </div>
          <FaMoneyBillWave style={{ position:'absolute', right:'-10px', bottom:'-10px', fontSize:'80px', color:'#fbbf24', opacity:0.05 }} />
        </div>
        <div style={{ background: 'linear-gradient(135deg, #7f1d1d, #450a0a)', padding: '25px', borderRadius: '16px', border: '1px solid #991b1b', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'relative', zIndex:2 }}>
            <p style={{ margin: '0 0 5px 0', color: '#fca5a5', fontSize: '12px', fontWeight: '700', textTransform:'uppercase' }}>Est. Meta API Cost</p>
            <h2 style={{ margin: 0, color: 'white', fontSize: '36px', fontWeight: '800' }}>₹{platformMetrics.globalSpend.toLocaleString(undefined, {maximumFractionDigits:0})}</h2>
            <p style={{ margin: '5px 0 0 0', fontSize:'12px', color: '#fecaca' }}>Payable to Zuckerberg</p>
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <FaSearch style={{ position: 'absolute', left: '15px', top: '14px', color: THEME.textSub }} />
          <input 
            type="text" 
            placeholder="Search Database (Name, Email, or UID)..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '12px 15px 12px 40px', background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: '8px', color: 'white', outline: 'none' }}
          />
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ padding: '12px 20px', background: THEME.card, border: `1px solid ${THEME.border}`, borderRadius: '8px', color: 'white', outline: 'none', cursor: 'pointer' }}>
          <option value="spend_desc">Highest Spenders</option>
          <option value="msgs_desc">Highest Volume</option>
          <option value="status">Connection Status</option>
        </select>
      </div>

      {/* MASTER TABLE (UPDATED COLUMNS) */}
      <div style={{ background: THEME.card, borderRadius: '16px', border: `1px solid ${THEME.border}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#0f172a', borderBottom: `1px solid ${THEME.border}`, color: THEME.textSub, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <th style={{ padding: '15px 25px' }}>Tenant Identity</th>
                <th style={{ padding: '15px 25px' }}>API Link</th>
                <th style={{ padding: '15px 25px' }}>Volume</th>
                <th style={{ padding: '15px 25px' }}>Est. Cost</th>
                <th style={{ padding: '15px 25px' }}>Status</th>
                <th style={{ padding: '15px 25px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedBusinesses.map((b) => (
                <tr key={b.uid} style={{ borderBottom: `1px solid ${THEME.border}`, transition: 'background 0.2s' }}>
                  <td style={{ padding: '20px 25px' }}>
                    <div style={{ fontWeight: '600', color: 'white', display:'flex', alignItems:'center', gap:'8px' }}>
                        <FaIdBadge color={THEME.primary} /> {b.displayName}
                    </div>
                    <div style={{ fontSize: '11px', color: THEME.textSub, marginTop:'4px', marginLeft:'24px' }}>{b.subDetail}</div>
                    <div style={{ fontSize: '10px', color: '#475569', fontFamily:'monospace', marginLeft:'24px' }}>{b.uid}</div>
                  </td>
                  <td style={{ padding: '20px 25px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', background: b.phoneId === "Connected" ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: b.phoneId === "Connected" ? THEME.success : THEME.danger, border: b.phoneId === "Connected" ? `1px solid ${THEME.success}` : `1px solid ${THEME.danger}` }}>
                      {b.phoneId === "Connected" ? "ONLINE" : "OFFLINE"}
                    </span>
                  </td>
                  <td style={{ padding: '20px 25px', color: '#38bdf8', fontWeight: '700' }}>
                    {b.totalSent.toLocaleString()}
                  </td>
                  <td style={{ padding: '20px 25px', color: '#fbbf24', fontFamily:'monospace' }}>
                    ₹{b.spend.toFixed(2)}
                  </td>
                  <td style={{ padding: '20px 25px' }}>
                    <span style={{ color: b.status === "Active" ? THEME.success : THEME.danger, fontWeight:'600', fontSize:'13px' }}>● {b.status}</span>
                  </td>
                  <td style={{ padding: '20px 25px', textAlign: 'right' }}>
                    <button onClick={() => openUserLogs(b)} style={{ background: 'transparent', border: `1px solid ${THEME.primary}`, color: THEME.primary, padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', transition:'0.2s' }}>
                      OPEN X-RAY
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- X-RAY MODAL (UNCHANGED, JUST RE-ADDED FOR COMPLETENESS) --- */}
      {selectedUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#020617', width: '100%', maxWidth: '1000px', borderRadius: '20px', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 50px 100px -20px rgba(0,0,0,0.7)' }}>
            
            <div style={{ padding: '25px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f172a' }}>
              <div>
                <h3 style={{ margin: '0 0 5px 0', color: 'white', fontSize: '20px' }}>Tenant X-Ray: {selectedUser.displayName}</h3>
                <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: THEME.textSub, fontFamily: 'monospace' }}>
                  <span>ID: {selectedUser.uid}</span>
                </div>
              </div>
              <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', color: THEME.textSub, fontSize: '24px', cursor: 'pointer' }}><FaTimes /></button>
            </div>

            <div style={{ display: 'flex', background: '#0f172a', borderBottom: `1px solid ${THEME.border}` }}>
              <button onClick={() => setModalTab('campaigns')} style={{ flex: 1, padding: '15px', background: modalTab === 'campaigns' ? '#1e293b' : 'transparent', color: modalTab === 'campaigns' ? '#38bdf8' : THEME.textSub, border: 'none', borderBottom: modalTab === 'campaigns' ? '2px solid #38bdf8' : 'none', fontWeight: 'bold', cursor: 'pointer', display:'flex', justifyContent:'center', alignItems:'center', gap:'8px' }}>
                <FaBullhorn /> CAMPAIGN LOGS
              </button>
              <button onClick={() => setModalTab('audience')} style={{ flex: 1, padding: '15px', background: modalTab === 'audience' ? '#1e293b' : 'transparent', color: modalTab === 'audience' ? '#10b981' : THEME.textSub, border: 'none', borderBottom: modalTab === 'audience' ? '2px solid #10b981' : 'none', fontWeight: 'bold', cursor: 'pointer', display:'flex', justifyContent:'center', alignItems:'center', gap:'8px' }}>
                <FaAddressBook /> AUDIENCE DATA
              </button>
            </div>

            <div style={{ padding: '0', overflowY: 'auto', flex: 1, background: '#020617' }}>
              {isModalLoading ? (
                <div style={{ textAlign: 'center', padding: '100px', color: THEME.textSub }}><FaSpinner className="fa-spin" size={30}/> <br/><br/>Decrypting Tenant Data...</div>
              ) : (
                <>
                  <div style={{ padding: '15px', borderBottom: `1px solid ${THEME.border}`, textAlign:'right' }}>
                     <button onClick={exportTenantData} style={{ background: '#1e293b', color: 'white', border: `1px solid ${THEME.border}`, padding: '8px 15px', borderRadius: '6px', fontSize:'11px', cursor:'pointer', fontWeight:'bold' }}><FaFileCsv style={{marginRight:'5px'}}/> DOWNLOAD THIS DATA</button>
                  </div>
                  
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ color: THEME.textSub, fontSize: '11px', textTransform:'uppercase', borderBottom: `1px solid ${THEME.border}`, background: '#0f172a' }}>
                        {modalTab === 'campaigns' ? (
                          <>
                            <th style={{ padding: '15px 25px' }}>Date</th>
                            <th style={{ padding: '15px 25px' }}>Campaign Name</th>
                            <th style={{ padding: '15px 25px' }}>Size</th>
                            <th style={{ padding: '15px 25px' }}>Sent</th>
                          </>
                        ) : (
                          <>
                            <th style={{ padding: '15px 25px' }}>Phone</th>
                            <th style={{ padding: '15px 25px' }}>Last Active</th>
                            <th style={{ padding: '15px 25px' }}>Source</th>
                            <th style={{ padding: '15px 25px' }}>Last Context</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {modalTab === 'campaigns' ? (
                        userLogs.length === 0 ? <tr><td colSpan="4" style={{padding:'40px', textAlign:'center', color:THEME.textSub}}>No campaigns found.</td></tr> :
                        userLogs.map(log => (
                          <tr key={log.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                            <td style={{ padding: '15px 25px', color: THEME.textSub, fontSize:'12px' }}>{log.date}</td>
                            <td style={{ padding: '15px 25px', color: 'white', fontWeight:'600' }}>{log.name}</td>
                            <td style={{ padding: '15px 25px', color: THEME.textSub }}>{log.totalContacts}</td>
                            <td style={{ padding: '15px 25px', color: '#38bdf8' }}>{log.sent}</td>
                          </tr>
                        ))
                      ) : (
                        userAudience.length === 0 ? <tr><td colSpan="4" style={{padding:'40px', textAlign:'center', color:THEME.textSub}}>No contacts found.</td></tr> :
                        userAudience.map(c => (
                          <tr key={c.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                            <td style={{ padding: '15px 25px', color: '#10b981', fontFamily:'monospace' }}><FaWhatsapp style={{marginRight:'5px'}}/> {c.phone}</td>
                            <td style={{ padding: '15px 25px', color: THEME.textSub, fontSize:'12px' }}>{c.date}</td>
                            <td style={{ padding: '15px 25px' }}><span style={{background:'#1e293b', padding:'2px 8px', borderRadius:'4px', fontSize:'10px', color: '#94a3b8'}}>{c.source || 'N/A'}</span></td>
                            <td style={{ padding: '15px 25px', color: THEME.textSub, fontSize:'12px', fontStyle:'italic' }}>{c.lastMessage?.substring(0, 30)}...</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SuperAdmin;